/**
 * Config Validator
 * 
 * Validates UI state to ensure all signals and conditions are complete
 * before allowing submission.
 */

import { Leg, OrGroups as UIOrGroups } from "../types/flowchart";
import { ConfiguredSignal, validateSignal, SIGNAL_REGISTRY } from "../../../../config/signals";
import { POSITION_DATA_SIGNALS } from "../../../../config/signals/registry";
import { isDynamicConditionComplete } from "../../../../config/signals/dynamicConditions";
import { TradeCondition as UITradeCondition } from "../flowchart/TradeConditions";
import { UIBacktestState } from "./configGenerator";
import { detectLegCycle } from "./leg_cyclic_graph";

/**
 * Validation error details
 */
export interface ValidationError {
  path: string;      // Path to the invalid field (e.g., "Leg 1.Entry.Group 1.Signal 1")
  message: string;   // Error message
}

/**
 * Validation warning details (non-blocking)
 */
export interface ValidationWarning {
  path: string;
  message: string;
}

/**
 * Validation result
 */
export interface ConfigValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

/**
 * Check if a signal is empty (no type selected)
 */
function isSignalEmpty(signal: ConfiguredSignal): boolean {
  return !signal.type || signal.type === "";
}

/**
 * Check if at least one signal in the groups has a selected type
 */
function hasConfiguredSignal(groups: UIOrGroups): boolean {
  for (const group of groups) {
    for (const signal of group) {
      if (!isSignalEmpty(signal)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Context for signal validation within OrGroups
 */
interface SignalValidationContext {
  category: "entry" | "exit";
  legIndex?: number; // undefined for strategy-level signals
  totalLegs: number;
}

function parseLegNumbers(legRef: unknown): number[] {
  if (legRef === undefined || legRef === null || legRef === "") return [];
  if (Array.isArray(legRef)) {
    return legRef
      .map((value) => Number(value))
      .filter((value) => Number.isInteger(value));
  }

  return String(legRef)
    .split(",")
    .map((value) => Number(value.trim()))
    .filter((value) => Number.isInteger(value));
}

/**
 * Validate signals in OrGroups format
 */
function validateOrGroups(
  groups: UIOrGroups,
  basePath: string,
  context: SignalValidationContext
): ValidationError[] {
  const errors: ValidationError[] = [];

  for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
    const group = groups[groupIdx];

    for (let signalIdx = 0; signalIdx < group.length; signalIdx++) {
      const signal = group[signalIdx];
      const path = `${basePath}.Group ${groupIdx + 1}.Signal ${signalIdx + 1}`;

      // Check if signal type is selected
      if (isSignalEmpty(signal)) {
        errors.push({
          path,
          message: "Signal type must be selected",
        });
        continue; // Skip further validation for this signal
      }

      // Check if signal has all required params and get detailed error message
      const validationResult = validateSignal(signal);
      const signalLabel = SIGNAL_REGISTRY[signal.type]?.label || signal.type;
      if (!validationResult.valid) {
        errors.push({
          path,
          message: validationResult.error || `Signal "${signalLabel}" has validation errors`,
        });
      }

      const legNumbers = parseLegNumbers(signal.params.leg_idx);
      const missingLegNumber = legNumbers.find((num) => num < 1 || num > context.totalLegs);
      if (missingLegNumber !== undefined) {
        errors.push({
          path,
          message: `Leg ${missingLegNumber} does not exist. Please correct the leg number.`,
        });
      }

      // Position-data signals used as entry signals require leg_idx referencing another leg
      if (context.category === "entry" && POSITION_DATA_SIGNALS.has(signal.type)) {
        const legIdx = signal.params.leg_idx;
        if (legIdx === undefined || legIdx === null || legIdx === "") {
          errors.push({
            path,
            message: `Entry signal "${signalLabel}" requires a Leg Number referencing another leg`,
          });
        } else if (context.legIndex !== undefined) {
          if (legNumbers.length === 1 && legNumbers[0] === context.legIndex + 1) {
            errors.push({
              path,
              message: `Entry signal "${signalLabel}" cannot reference the same leg (Leg ${context.legIndex + 1}). It must reference another leg.`,
            });
          }
          if (legNumbers.length === 0) {
            errors.push({
              path,
              message: `Entry signal "${signalLabel}" requires a valid Leg Number referencing another leg`,
            });
          }
        }
      }
    }
  }

  return errors;
}

/**
 * Validate trade condition dynamic conditions and greek values
 */
function validateTradeCondition(
  condition: UITradeCondition,
  basePath: string,
  currentLegIndex: number,
  totalLegs: number
): ValidationError[] {
  const errors: ValidationError[] = [];
  const hasGreekConditionsConfigured = !!condition.greek ||
    !!condition.greekConditions?.some((gc) => !!gc.greek);

  // 1. Validate that option type is selected for Options
  if (condition.instrumentType === "Option" && !condition.optionType) {
    errors.push({
      path: basePath,
      message: "Option type (Call/Put) must be selected",
    });
  }

  // 2. Validate greek conditions for Options
  if (condition.instrumentType === "Option" && condition.greekConditions) {
    const isPut = condition.optionType === "Put";

    for (let i = 0; i < condition.greekConditions.length; i++) {
      const gc = condition.greekConditions[i];
      const gcPath = `${basePath}.Greek Condition ${i + 1}`;

      // 2a. Validate that greek type is selected if condition exists
      if (!gc.greek) {
        errors.push({
          path: gcPath,
          message: "Greek type must be selected",
        });
        continue; // Skip further validation for this condition
      }

      // 2b. Validate that greek value is provided if greek type is selected
      if (gc.greek !== "Dynamic" && (gc.greekValue === undefined || gc.greekValue === null || gc.greekValue === 0)) {
        errors.push({
          path: gcPath,
          message: `Value must be provided for ${gc.greek}`,
        });
        continue; // Skip further validation for this condition
      }

      // 2c. Validate delta range (UI always uses 0 to 1 for both Call and Put)
      // For Put options, the value is automatically negated when generating the config
      if (gc.greek === "delta" && gc.greekValue !== undefined && gc.greekValue !== null) {
        const deltaValue = Math.abs(gc.greekValue); // Normalize: UI may store negative for puts

        if (deltaValue < 0 || deltaValue > 1) {
          errors.push({
            path: gcPath,
            message: `Delta must be between 0 and 1 (current: ${deltaValue})${isPut ? '. It will be inferred as negative for Put options.' : ''}`,
          });
        }
      }
    }
  }

  // Option contract selection must have at least one greek filter
  // when dynamic conditions are disabled.
  if (condition.instrumentType === "Option" && !condition.dynamicEnabled && !hasGreekConditionsConfigured) {
    errors.push({
      path: basePath,
      message: "No greek condition filter to determine a unique contract",
    });
  }

  // Validate dynamic conditions if enabled
  if (condition.dynamicEnabled && condition.dynamicConditions) {
    for (let i = 0; i < condition.dynamicConditions.length; i++) {
      const dynCondition = condition.dynamicConditions[i];
      const path = `${basePath}.Dynamic Condition ${i + 1}`;

      // Skip empty dynamic conditions
      if (!dynCondition.type) {
        continue;
      }

      if (!isDynamicConditionComplete(dynCondition, currentLegIndex)) {
        errors.push({
          path,
          message: `Dynamic condition "${dynCondition.type}" has missing required parameters`,
        });
      }

      // Special validation for delta_hedge_quantity: cannot hedge same leg
      if (dynCondition.type === "delta_hedge_quantity") {
        const legIdx = dynCondition.params.leg_idx;
        if (Array.isArray(legIdx)) {
          const parsedLegNumbers = legIdx
            .map((value) => Number(value))
            .filter((value) => Number.isInteger(value));
          const missingLegNumber = parsedLegNumbers.find((num) => num < 1 || num > totalLegs);
          if (missingLegNumber !== undefined) {
            errors.push({
              path,
              message: `Leg ${missingLegNumber} does not exist. Please correct the leg number.`,
            });
          }
          if (parsedLegNumbers.includes(currentLegIndex + 1)) {
            errors.push({
              path,
              message: `Cannot hedge leg ${currentLegIndex + 1} with itself`,
            });
          }
        }
      }

      // Special validation for strike_relative_to_leg: cannot reference same leg
      if (dynCondition.type === "strike_relative_to_leg") {
        const legIdx = dynCondition.params.leg_idx;
        const selectedLegNumber = typeof legIdx === 'number' ? legIdx : parseInt(String(legIdx), 10);
        if (!isNaN(selectedLegNumber)) {
          if (selectedLegNumber < 1 || selectedLegNumber > totalLegs) {
            errors.push({
              path,
              message: `Leg ${selectedLegNumber} does not exist. Please correct the leg number.`,
            });
          }
          if (selectedLegNumber === currentLegIndex + 1) {
            errors.push({
              path,
              message: `Cannot reference the same leg (Leg ${currentLegIndex + 1}). Please select a different leg number.`,
            });
          }
        }
      }

      // Special validation for strike_relative_to_spot
      if (dynCondition.type === "strike_relative_to_spot") {
        const absolute = dynCondition.params.absolute;
        const relative = dynCondition.params.relative;

        // At least one of absolute or relative must be provided
        if (absolute === undefined && relative === undefined) {
          errors.push({
            path,
            message: `At least one of 'Absolute Adjustment' or 'Relative Adjustment' must be provided`,
          });
        }

        // Both cannot be provided together
        if (absolute !== undefined && relative !== undefined) {
          errors.push({
            path,
            message: `Cannot provide both 'Absolute Adjustment' and 'Relative Adjustment' together. Choose one.`,
          });
        }
      }
    }
  }

  return errors;
}

/**
 * Validate a single leg
 */
function validateLeg(leg: Leg, legIndex: number, hasGlobalSymbols: boolean, totalLegs: number): ValidationError[] {
  const errors: ValidationError[] = [];
  const basePath = `Leg ${legIndex + 1} (${leg.name})`;

  // Validate entry signals (with context for position-data signal validation)
  errors.push(...validateOrGroups(leg.entryRules, `${basePath}.Entry`, { category: "entry", legIndex, totalLegs }));

  // Validate exit signals
  errors.push(...validateOrGroups(leg.exitRules, `${basePath}.Exit`, { category: "exit", legIndex, totalLegs }));

  // Validate trade conditions (pass legIndex for self-hedge validation)
  for (let i = 0; i < leg.tradeConditions.length; i++) {
    const condition = leg.tradeConditions[i];
    const conditionPath = `${basePath}.Trade Condition ${i + 1}`;

    errors.push(
      ...validateTradeCondition(
        condition,
        conditionPath,
        legIndex,
        totalLegs
      )
    );

    // Validate symbol: if no global symbols, each leg must have a symbol
    if (!hasGlobalSymbols && (!condition.symbol || condition.symbol.trim() === "")) {
      errors.push({
        path: conditionPath,
        message: "Symbol must be selected (no strategy-level symbol defined)",
      });
    }
  }

  return errors;
}

/**
 * Main validation function: Validate the entire UI state
 */
export function validateBacktestConfig(state: UIBacktestState): ConfigValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationWarning[] = [];
  const totalLegs = state.legs.length;

  if (totalLegs === 0) {
    errors.push({
      path: "Strategy Legs",
      message: "Add at least one leg. There is nothing to trade on.",
    });
  }

  // Check if there are global symbols defined at strategy level
  const hasGlobalSymbols = state.symbolBank && state.symbolBank.some(s => s && s.trim() !== "");

  // Strategy entry signals are optional:
  // if none are configured, backend defaults to `on_exit` (Period Start / On Exit).
  const hasStrategyEntrySignals = hasConfiguredSignal(state.strategyEntryRules);
  if (hasStrategyEntrySignals) {
    errors.push(...validateOrGroups(state.strategyEntryRules, "Strategy.Entry", { category: "entry", totalLegs }));
  } else {
    warnings.push({
      path: "Strategy.Entry",
      message: state.intradayEnabled
        ? "No intraday strategy entry selected. The strategy can be scheduled but will not enter until an entry signal is added."
        : "No strategy entry selected. It will default to Period Start / On Exit (on_exit).",
    });
  }

  // Validate strategy-level exit signals
  errors.push(...validateOrGroups(state.strategyExitRules, "Strategy.Exit", { category: "exit", totalLegs }));

  // Validate each leg
  for (let i = 0; i < state.legs.length; i++) {
    errors.push(...validateLeg(state.legs[i], i, hasGlobalSymbols, totalLegs));
  }

  const cycleDetectionResult = detectLegCycle(state);
  if (cycleDetectionResult) {
    errors.push({
      path: "Leg Dependency Graph",
      message: cycleDetectionResult.message,
    });
  }

  // leg_signals_case: if at least one leg has leg-level entry/exit signals,
  // warn for each leg that does not define any leg-level entry signal.
  const isLegSignalsCase = state.legs.some(
    (leg) => hasConfiguredSignal(leg.entryRules) || hasConfiguredSignal(leg.exitRules)
  );

  if (isLegSignalsCase) {
    for (let i = 0; i < state.legs.length; i++) {
      const leg = state.legs[i];
      if (!hasConfiguredSignal(leg.entryRules)) {
        warnings.push({
          path: `Leg ${i + 1} (${leg.name}).Entry`,
          message: `Leg ${i + 1} will never start here because it is missing an entry signal.`,
        });
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Quick check if config is valid (without detailed errors)
 */
export function isConfigValid(state: UIBacktestState): boolean {
  return validateBacktestConfig(state).valid;
}
